import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import WorkCard from '@/components/feature/WorkCard';
import SectionHeading from '@/components/base/SectionHeading';
import { usePageMeta } from '@/hooks/usePageMeta';
import {
  TYPE_META,
  REGION_OPTIONS,
  filterWorks,
  getPopularWorks,
  getYearOptions,
  searchWorks,
} from '@/lib/works';
import type { Region, WorkType } from '@/types/work';

const CONTAINER = 'mx-auto w-full max-w-[1240px] px-4 md:px-8 lg:px-10';

const SELECT_CLASS =
  'h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-800 transition-colors focus:border-primary-400 focus:outline-none cursor-pointer';

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get('q') || '';

  const [input, setInput] = useState(keyword);
  const [type, setType] = useState<WorkType | ''>('');
  const [region, setRegion] = useState<Region | ''>('');
  const [year, setYear] = useState('');

  usePageMeta(
    keyword ? `“${keyword}” 搜索结果 - 日韩中文字幕` : '搜索日韩影视字幕 - 日韩中文字幕',
    keyword
      ? `查看与“${keyword}”相关的日韩影视中文字幕资源，可按类型、地区与年份进一步筛选。`
      : '搜索日韩影视作品的中文字幕资源，支持标题、原名、题材与标签关键词。'
  );

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  const updateKeyword = (value: string) => {
    setInput(value);
    const next = new URLSearchParams(searchParams);
    if (value.trim()) next.set('q', value);
    else next.delete('q');
    setSearchParams(next, { replace: true });
  };

  const years = useMemo(() => getYearOptions(), []);
  const popular = getPopularWorks(4);

  const baseResults = useMemo(() => (keyword ? searchWorks(keyword) : []), [keyword]);

  const results = useMemo(() => {
    return filterWorks(baseResults, {
      type: type || undefined,
      region: region || undefined,
      yearFrom: year ? Number(year) : undefined,
      yearTo: year ? Number(year) : undefined,
    });
  }, [baseResults, type, region, year]);

  const hasFilter = Boolean(type || region || year);
  const resetFilters = () => {
    setType('');
    setRegion('');
    setYear('');
  };

  return (
    <>
      <section className="border-b border-background-200 bg-background-100">
        <div className={`${CONTAINER} py-8 md:py-10`}>
          <Breadcrumb items={[{ label: '主页', to: '/' }, { label: '搜索' }]} />
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            搜索影视字幕资源
          </h1>
          <p className="mt-2 text-sm text-foreground-600">
            输入作品名、原名、题材或标签关键词，即可快速查找对应的中文字幕信息。
          </p>

          <div className="mt-5 flex w-full items-center gap-2 rounded-full border border-background-300 bg-background-50 p-1.5 md:max-w-2xl">
            <i className="ri-search-line ml-3 text-lg text-foreground-400"></i>
            <input
              type="search"
              value={input}
              onChange={(event) => updateKeyword(event.target.value)}
              placeholder="例如：铃芽之旅、寄生虫、综艺、动画"
              aria-label="搜索影视作品"
              className="h-12 min-w-0 flex-1 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none md:text-base"
            />
            {input && (
              <button
                type="button"
                aria-label="清空关键词"
                onClick={() => updateKeyword('')}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-foreground-500 transition-colors hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            )}
          </div>

          {keyword && (
            <p className="mt-4 text-sm text-foreground-600">
              当前搜索：<strong className="font-semibold text-primary-600">{keyword}</strong>
              <span className="ml-3 text-foreground-500">
                共找到 <strong className="font-semibold text-foreground-950">{results.length}</strong> 条结果
              </span>
            </p>
          )}
        </div>
      </section>

      <section className={`${CONTAINER} py-8 md:py-10`}>
        {keyword && (
          <div className="mb-6 rounded-lg border border-background-200 bg-background-50 p-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <label className="flex flex-col gap-1.5">
                <span className="text-xs font-medium text-foreground-500">类型</span>
                <select className={SELECT_CLASS} value={type} onChange={(e) => setType(e.target.value as WorkType | '')}>
                  <option value="">全部类型</option>
                  <option value="movie">{TYPE_META.movie.label}</option>
                  <option value="tv">{TYPE_META.tv.label}</option>
                  <option value="variety">{TYPE_META.variety.label}</option>
                </select>
              </label>
              <label className="flex flex-col gap-1.5">
                <span className="text-xs font-medium text-foreground-500">地区</span>
                <select
                  className={SELECT_CLASS}
                  value={region}
                  onChange={(e) => setRegion(e.target.value as Region | '')}
                >
                  <option value="">全部地区</option>
                  {REGION_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1.5">
                <span className="text-xs font-medium text-foreground-500">年份</span>
                <select className={SELECT_CLASS} value={year} onChange={(e) => setYear(e.target.value)}>
                  <option value="">全部年份</option>
                  {years.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            {hasFilter && (
              <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-4">
                <span className="text-xs text-foreground-500">已应用筛选条件</span>
                <button
                  type="button"
                  onClick={resetFilters}
                  className="inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-3.5 text-xs font-medium text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600 cursor-pointer"
                >
                  <i className="ri-close-line"></i>
                  清空筛选
                </button>
              </div>
            )}
          </div>
        )}

        {keyword && results.length > 0 && (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 md:gap-5">
            {results.map((work) => (
              <WorkCard key={work.id} work={work} />
            ))}
          </div>
        )}

        {keyword && results.length === 0 && (
          <div className="flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-search-eye-line text-3xl"></i>
            </span>
            <h2 className="mt-5 font-heading text-lg font-bold text-foreground-950">未找到相关字幕资源</h2>
            <p className="mt-2 max-w-md text-sm text-foreground-500">
              没有与“{keyword}”匹配的作品{hasFilter ? '，也可能被当前筛选条件排除了' : ''}。换个关键词，或看看下面的热门推荐吧。
            </p>
            {hasFilter && (
              <button
                type="button"
                onClick={resetFilters}
                className="mt-5 inline-flex h-10 items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600 cursor-pointer"
              >
                <i className="ri-refresh-line"></i>
                清空筛选条件
              </button>
            )}
          </div>
        )}

        {!keyword && (
          <div className="rounded-lg border border-dashed border-background-300 bg-background-50 py-12 text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-search-line text-2xl"></i>
            </span>
            <h2 className="mt-4 font-heading text-base font-bold text-foreground-950">开始你的查找</h2>
            <p className="mt-2 text-sm text-foreground-500">在上方输入关键词，或试试下面这些热门作品。</p>
          </div>
        )}

        {(!keyword || results.length === 0) && (
          <div className="mt-12">
            <SectionHeading title="热门推荐" subtitle="近期热度最高的日韩影视中文字幕资源" />
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 md:gap-5">
              {popular.map((work) => (
                <WorkCard key={work.id} work={work} />
              ))}
            </div>
          </div>
        )}
      </section>
    </>
  );
}